import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis,
  CartesianGrid, ReferenceLine,
} from 'recharts';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import Supercluster from 'supercluster';
import { useApi } from '../apiHelpers';
import {
  stringToColor,
  getDronePosition,
  isLive,
  createDroneIcon,
  createClusterIcon,
} from '../DroneMap/mapHelpers';
import useFleetStore from '../stores/useFleetStore';
import { useNightlyReload } from '../hooks/useNightlyReload';
import { parseDate, formatEventDate, getDisplayStatus } from '../utils/date-utils';
import dayjs from 'dayjs';
import fireflyLogo from '../img/firefly-logo.png';
import Panel from '../components/ui/Panel';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import Appear from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import 'leaflet/dist/leaflet.css';

const POLL_INTERVAL = 60 * 1000; // 60 seconds

/* ── Color helpers (same thresholds as ForecastChart) ────────── */
const getAvailColor = (available, total) => {
  if (total <= 0) return '#5f5f5f';
  const ratio = available / total;
  if (ratio <= 0) return '#ef4444';
  if (ratio <= 0.10) return '#f97316';
  if (ratio <= 0.25) return '#eab308';
  return '#34d399';
};

const getAvailColorClass = (available, total) => {
  if (total <= 0) return 'text-on-surface-variant';
  const ratio = available / total;
  if (ratio <= 0) return 'text-red-400';
  if (ratio <= 0.10) return 'text-orange-400';
  if (ratio <= 0.25) return 'text-yellow-400';
  return 'text-emerald-400';
};

const getBatteryAvailColor = (available, total) => {
  if (total <= 0) return '#5f5f5f';
  const ratio = available / total;
  if (ratio <= 0) return '#f87171';
  if (ratio <= 0.10) return '#fb923c';
  if (ratio <= 0.25) return '#facc15';
  return '#6ee7b7';
};

const snapTo500 = (val) => Math.ceil(val / 500) * 500;

/* ── Map helpers ────────────────────────────────────────────── */
const LIVE_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour (shop floor is stricter than /map's 2h)

/** Filter raw recent-locations data to drones with valid coordinates. */
function validLocations(locations) {
  return (locations || []).filter((drone) => {
    if (drone.last_seen_server?.startsWith('Maintenance by')) return true;
    if (!drone.last_seen_location) return false;
    const parts = drone.last_seen_location.split(',');
    if (parts.length !== 2) return false;
    const lat = parseFloat(parts[0].trim());
    const lon = parseFloat(parts[1].trim());
    return !isNaN(lat) && !isNaN(lon);
  });
}

/**
 * Group live drones within ~1 mile into tour stops for the auto-pan.
 * Returns array of { bounds: LatLngBounds, count }.
 */
function findLiveTourStops(drones) {
  const MILE_DEG = 0.0145; // ~1 mile in degrees latitude
  const livePoints = [];
  drones.forEach((d) => {
    if (!isLive(d, LIVE_THRESHOLD_MS)) return;
    const pos = getDronePosition(d);
    if (pos) livePoints.push(pos);
  });
  if (livePoints.length === 0) return [];

  // Greedy clustering: assign each point to nearest existing cluster or create new
  const clusters = [];
  for (const [lat, lon] of livePoints) {
    let added = false;
    for (const c of clusters) {
      if (Math.abs(lat - c.centerLat) < MILE_DEG && Math.abs(lon - c.centerLon) < MILE_DEG) {
        c.points.push([lat, lon]);
        c.centerLat = c.points.reduce((s, p) => s + p[0], 0) / c.points.length;
        c.centerLon = c.points.reduce((s, p) => s + p[1], 0) / c.points.length;
        added = true;
        break;
      }
    }
    if (!added) {
      clusters.push({ centerLat: lat, centerLon: lon, points: [[lat, lon]] });
    }
  }

  return clusters.map((c) => ({
    bounds: L.latLngBounds(c.points.map((p) => L.latLng(p[0], p[1]))).pad(0.3),
    count: c.points.length,
  }));
}

/** Zoom-aware clustered markers using Supercluster (same as /map). */
function ShopFloorClusterMarkers({ supercluster, drones }) {
  const map = useMap();
  const [clusters, setClusters] = useState([]);

  const updateClusters = useCallback(() => {
    if (!supercluster || drones.length === 0) {
      setClusters([]);
      return;
    }
    const bounds = map.getBounds();
    const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
    const currentZoom = Math.floor(map.getZoom());
    setClusters(supercluster.getClusters(bbox, currentZoom));
  }, [map, supercluster, drones.length]);

  useMapEvents({ moveend: updateClusters, zoomend: updateClusters });

  useEffect(() => {
    updateClusters();
  }, [updateClusters]);

  return (
    <>
      {clusters.map((cluster) => {
        const [lon, lat] = cluster.geometry.coordinates;
        const position = [lat, lon];

        if (cluster.properties.cluster) {
          const { cluster_id, point_count, dominantColor, hasLive } = cluster.properties;
          return (
            <Marker
              key={`cluster-${cluster_id}`}
              position={position}
              icon={createClusterIcon(point_count, dominantColor, hasLive)}
            />
          );
        }

        const drone = cluster.properties;
        const color = stringToColor(drone.last_seen_server);
        return (
          <Marker
            key={drone.serialnumber}
            position={position}
            icon={createDroneIcon(color, false)} // no ping on individuals — cluster ping is enough
          />
        );
      })}
    </>
  );
}

/** Auto-tour: cycles through live drone groups, zooming in for 15s each. */
function MapAutoTour({ allPositions, liveStops }) {
  const map = useMap();
  const initializedRef = useRef(false);

  // Initial fit to all positions
  useEffect(() => {
    if (initializedRef.current || allPositions.length === 0) return;
    if (allPositions.length === 1) map.setView(allPositions[0], 10);
    else map.fitBounds(L.latLngBounds(allPositions), { padding: [30, 30] });
    initializedRef.current = true;
  }, [map, allPositions]);

  // Auto-tour cycle: overview(5s) → zoom(15s) → overview(5s) → zoom(15s) → ...
  useEffect(() => {
    if (liveStops.length === 0 || allPositions.length === 0) return;

    const overviewBounds = L.latLngBounds(allPositions);
    let timer = null;
    let idx = 0;
    let cancelled = false;

    const zoomToStop = () => {
      if (cancelled) return;
      const stop = liveStops[idx % liveStops.length];
      map.flyToBounds(stop.bounds, { maxZoom: 18, duration: 1.5 });
      timer = setTimeout(zoomOut, 15000);
    };

    const zoomOut = () => {
      if (cancelled) return;
      map.flyToBounds(overviewBounds, { padding: [30, 30], duration: 1.5 });
      idx = (idx + 1) % liveStops.length;
      timer = setTimeout(zoomToStop, 5000);
    };

    timer = setTimeout(zoomToStop, 3000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [map, liveStops, allPositions]);

  return null;
}

/* ── Status color maps ──────────────────────────────────────── */
const eventStatusColors = {
  pending:   'bg-[rgb(255_255_255/0.06)] text-on-surface-variant ring-[rgb(255_255_255/0.12)]',
  confirmed: 'bg-blue-500/10 text-blue-400 ring-blue-500/20',
  packing:   'bg-orange-500/10 text-orange-400 ring-orange-500/20',
  active:    'bg-green-500/10 text-green-400 ring-green-500/20',
  completed: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/20',
};

const orderStatusColors = {
  pending:       'bg-[rgb(255_255_255/0.06)] text-on-surface-variant ring-[rgb(255_255_255/0.12)]',
  confirmed:     'bg-blue-500/10 text-blue-400 ring-blue-500/20',
  in_production: 'bg-orange-500/10 text-orange-400 ring-orange-500/20',
  ready:         'bg-green-500/10 text-green-400 ring-green-500/20',
  shipping:      'bg-yellow-500/10 text-yellow-400 ring-yellow-500/20',
  shipped:       'bg-purple-500/10 text-purple-400 ring-purple-500/20',
};

const orderTypeBadge = {
  sale:   'bg-primary/15 text-primary-ink',
  lease:  'bg-blue-500/15 text-blue-400',
  rental: 'bg-orange-500/15 text-orange-400',
};

const flagColors = [
  'bg-orange-500', 'bg-red-500', 'bg-yellow-500', 'bg-purple-500',
  'bg-blue-500', 'bg-pink-500', 'bg-teal-500', 'bg-indigo-500',
];

/* ── Shop Marquee ───────────────────────────────────────────── */
const ShopMarquee = ({ available, flagCount, droneCount, buildCaps }) => {
  const Messages = () => (
    <>
      <span className="px-8 text-primary font-bold whitespace-nowrap tracking-wider text-lg">
        SHOP FLOOR
      </span>
      <span className="text-gray-700 px-2">&bull;</span>
      <span className="px-6 text-on-surface-variant whitespace-nowrap text-base">
        Available: <span className="text-emerald-400 font-semibold">{available ?? '\u2014'}</span>
      </span>
      <span className="text-gray-700 px-2">&bull;</span>
      <span className="px-6 text-on-surface-variant whitespace-nowrap text-base">
        Fleet: <span className="text-primary font-semibold">{droneCount}</span>
      </span>
      <span className="text-gray-700 px-2">&bull;</span>
      <span className="px-6 whitespace-nowrap text-base">
        Flags:{' '}
        <span className={`font-semibold ${flagCount > 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
          {flagCount}
        </span>
      </span>
      <span className="text-gray-700 px-2">&bull;</span>
      {buildCaps.length > 0 && (
        <>
          <span className="px-6 text-on-surface-variant whitespace-nowrap text-base">
            Build Cap:{' '}
            {buildCaps.map((c, i) => (
              <span key={c.code} className={i > 0 ? 'ml-3' : ''}>
                {c.code}{' '}
                <span className="text-blue-400 font-semibold">{c.builds}</span>
              </span>
            ))}
          </span>
          <span className="text-gray-700 px-2">&bull;</span>
        </>
      )}
      <span className="px-8 text-primary font-bold whitespace-nowrap tracking-wide text-lg">
        DO DRONE SHOWS
      </span>
      <span className="text-gray-700 px-2">&bull;</span>
    </>
  );

  return (
    <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a] border border-gray-800">
      <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#1a1a1a] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#1a1a1a] to-transparent z-10 pointer-events-none" />
      <div className="marquee-track flex py-2.5 items-center" style={{ minWidth: '200vw' }}>
        <div className="flex items-center shrink-0" style={{ minWidth: '100vw' }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center shrink-0"><Messages /></div>
          ))}
        </div>
        <div className="flex items-center shrink-0" style={{ minWidth: '100vw' }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center shrink-0"><Messages /></div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Section heading ────────────────────────────────────────── */
const Heading = ({ color = 'bg-primary/60', children, right }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      <div className={`w-1 h-3.5 rounded-full ${color}`} />
      <h3 className="text-xs text-on-surface-variant uppercase tracking-widest font-semibold">{children}</h3>
    </div>
    {right}
  </div>
);

/* ── Card wrapper ───────────────────────────────────────────── */
const Card = ({ children, className = '', label }) => (
  <Panel label={label} className={`overflow-hidden ${className}`}>
    {children}
  </Panel>
);

/* ================================================================ */
/*  MAIN COMPONENT                                                   */
/* ================================================================ */
const ShopFloor = () => {
  const api = useApi();
  const drones = useFleetStore((s) => s.drones);
  const flags = useFleetStore((s) => s.flags);
  const refreshFleet = useFleetStore((s) => s.refreshFleet);

  const [forecast, setForecast] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [events, setEvents] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clock, setClock] = useState(new Date());


  /* ── Fetch all data in parallel ────────────────────────────── */
  const fetchAll = useCallback(async () => {
    try {
      const [forecastData, dashboard, eventsData, salesOrders, leaseOrders, locationData] = await Promise.all([
        api.get('/dashboard/forecast', { silent: true }),
        api.get('/dashboard', { silent: true }),
        api.get('/events', { silent: true }),
        api.get('/orders?order_type=sale', { silent: true }),
        api.get('/orders?order_type=lease', { silent: true }),
        api.get('/fleet/recent-locations', { silent: true }),
      ]);

      setForecast(forecastData);
      setDashData(dashboard);
      if (locationData) setLocations(locationData);

      const now = dayjs();
      const today = dayjs.utc().startOf('day');

      // Upcoming events: today onward, not cancelled, nearest first.
      // Day-granularity UTC compare so *today's* shows aren't dropped once
      // local time crosses midnight-UTC (parseDate returns a UTC start-of-day).
      setEvents(
        (eventsData || [])
          .filter(e => {
            const d = parseDate(e.event_date);
            return d && !d.isBefore(today) && e.status !== 'cancelled';
          })
          .sort((a, b) => parseDate(a.event_date)?.diff(parseDate(b.event_date)))
          .slice(0, 6)
      );

      // Upcoming deliveries: merge sales + leases with future delivery dates
      const allOrders = [...(salesOrders || []), ...(leaseOrders || [])];
      setDeliveries(
        allOrders
          .filter(o =>
            o.delivery_date &&
            !['delivered', 'complete', 'cancelled', 'returned'].includes(o.status) &&
            parseDate(o.delivery_date)?.isAfter(now.subtract(1, 'day'))
          )
          .sort((a, b) => parseDate(a.delivery_date)?.diff(parseDate(b.delivery_date)))
          .slice(0, 5)
      );
    } catch (err) {
      console.error('[Shop Floor] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial fetch + polling
  useEffect(() => {
    fetchAll();
    const dataInterval = setInterval(fetchAll, POLL_INTERVAL);
    const fleetInterval = setInterval(refreshFleet, POLL_INTERVAL);
    return () => {
      clearInterval(dataInterval);
      clearInterval(fleetInterval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clock (update every 30s)
  useEffect(() => {
    const interval = setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  // Nightly kiosk reload at 4am to reset accumulated browser state.
  useNightlyReload(4);

  /* ── Derived data ──────────────────────────────────────────── */
  const fleetCounts = dashData?.fleetCounts || {};
  const buildCapacity = dashData?.buildCapacity || {};
  const internalFleet = fleetCounts.internalFleet;
  const available = internalFleet != null
    ? internalFleet - (fleetCounts.totalLeased || 0) - (fleetCounts.totalRentalCommitted || 0) - (fleetCounts.activeFlagCount || 0)
    : null;

  /* ── Chart data ────────────────────────────────────────────── */
  const chartData = useMemo(() => {
    if (!forecast?.series?.length) return [];
    return forecast.series.map(d => ({
      ...d,
      availableBatteries: d.totalBatteries - d.committedBatteries,
      availablePyro: (forecast.totalPyro || 0) - (d.committedPyro || 0),
    }));
  }, [forecast]);

  const todayStr = new Date().toISOString().split('T')[0];
  const todayData = useMemo(
    () => chartData.find(d => d.date === todayStr) || chartData[0],
    [chartData, todayStr]
  );

  // Filter to drones with valid coordinates (Maintenance drones map to fixed location)
  const validDrones = useMemo(() => validLocations(locations), [locations]);

  // Positions for bounds fitting
  const mapPositions = useMemo(
    () => validDrones.map((d) => getDronePosition(d)),
    [validDrones]
  );

  // Live drones grouped into tour stops (~1 mile radius).
  // Memoize on a positional signature so polling (every 60s) doesn't change the
  // liveStops reference when the underlying live drones haven't moved, which would
  // otherwise tear down the MapAutoTour and restart from stop 0 every poll.
  const liveSignature = useMemo(() => {
    return validDrones
      .filter((d) => isLive(d, LIVE_THRESHOLD_MS))
      .map((d) => {
        const [lat, lon] = getDronePosition(d);
        return `${d.serialnumber}:${lat.toFixed(4)},${lon.toFixed(4)}`;
      })
      .sort()
      .join('|');
  }, [validDrones]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const liveStops = useMemo(() => findLiveTourStops(validDrones), [liveSignature]);

  // Supercluster index — zoom-aware clustering with dominant color per cluster
  const supercluster = useMemo(() => {
    if (validDrones.length === 0) return null;
    const points = validDrones.map((drone) => {
      const pos = getDronePosition(drone);
      return {
        type: 'Feature',
        properties: {
          ...drone,
          color: stringToColor(drone.last_seen_server),
          isLive: isLive(drone, LIVE_THRESHOLD_MS),
        },
        geometry: { type: 'Point', coordinates: [pos[1], pos[0]] }, // GeoJSON: [lon, lat]
      };
    });
    const index = new Supercluster({
      radius: 40,
      maxZoom: 16,
      minZoom: 0,
      map: (props) => ({ colors: { [props.color]: 1 }, hasLive: props.isLive ? 1 : 0 }),
      reduce: (acc, props) => {
        for (const [color, count] of Object.entries(props.colors)) {
          acc.colors[color] = (acc.colors[color] || 0) + count;
        }
        acc.hasLive = acc.hasLive || props.hasLive;
      },
    });
    index.load(points);
    return {
      getClusters: (bbox, zoom) => {
        return index.getClusters(bbox, zoom).map((cluster) => {
          if (cluster.properties.cluster) {
            const colors = cluster.properties.colors || {};
            let dominantColor = '#888888';
            let maxCount = 0;
            for (const [color, count] of Object.entries(colors)) {
              if (count > maxCount) { maxCount = count; dominantColor = color; }
            }
            cluster.properties.dominantColor = dominantColor;
            cluster.properties.hasLive = !!cluster.properties.hasLive;
          }
          return cluster;
        });
      },
    };
  }, [validDrones]);

  const liveCount = useMemo(
    () => liveStops.reduce((sum, s) => sum + s.count, 0),
    [liveStops]
  );

  const formatChartDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Gradient stops for color-shifting lines
  const availGradientStops = useMemo(() => {
    if (!chartData.length) return [];
    const stops = [];
    let prevColor = null;
    chartData.forEach((d, i) => {
      const color = getAvailColor(d.availableDrones, d.totalFleet);
      const offset = chartData.length > 1 ? i / (chartData.length - 1) : 0;
      if (color !== prevColor) {
        if (prevColor) stops.push({ offset: `${(offset * 100).toFixed(2)}%`, color: prevColor });
        stops.push({ offset: `${(offset * 100).toFixed(2)}%`, color });
        prevColor = color;
      }
    });
    return stops;
  }, [chartData]);

  const battGradientStops = useMemo(() => {
    if (!chartData.length) return [];
    const stops = [];
    let prevColor = null;
    chartData.forEach((d, i) => {
      const color = getBatteryAvailColor(d.availableBatteries, d.totalBatteries);
      const offset = chartData.length > 1 ? i / (chartData.length - 1) : 0;
      if (color !== prevColor) {
        if (prevColor) stops.push({ offset: `${(offset * 100).toFixed(2)}%`, color: prevColor });
        stops.push({ offset: `${(offset * 100).toFixed(2)}%`, color });
        prevColor = color;
      }
    });
    return stops;
  }, [chartData]);

  // Y-axis bounds
  const [yMin, yMax] = useMemo(() => {
    if (!chartData.length) return [0, 500];
    const allAvail = chartData.map(d => d.availableDrones);
    const allBatt = chartData.map(d => d.availableBatteries);
    const allPyro = chartData.map(d => d.availablePyro);
    const minVal = Math.min(...allAvail, ...allBatt, ...allPyro);
    const maxVal = Math.max(forecast?.totalFleet || 0, forecast?.totalBatteries || 0);
    return [minVal < 0 ? -snapTo500(Math.abs(minVal)) : 0, snapTo500(maxVal)];
  }, [chartData, forecast]);

  /* ── Flag categories ───────────────────────────────────────── */
  const flagCategories = useMemo(() => {
    const cats = flags.reduce((acc, f) => {
      const cat = f.descript || 'Uncategorized';
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(cats).sort((a, b) => b[1] - a[1]);
  }, [flags]);

  /* ── Parts capacity ────────────────────────────────────────── */
  const capModels = buildCapacity.products || [];
  // Headline: up to three models, each at its lowest variant figure.
  const buildCaps = capModels
    .filter(m => m.capacity.length > 0)
    .slice(0, 3)
    .map(m => ({ code: m.model_code || m.sku, builds: Math.min(...m.capacity.map(c => c.builds)) }));
  const limitingLineIds = new Set(
    capModels.flatMap(m => m.capacity.map(c => c.limited_by?.line_id)).filter(id => id != null)
  );
  // Lines arrive lowest first within a model; the panel wants the lowest across all of them.
  const parts = capModels
    .flatMap(m => m.lines.map(l => ({ ...l, model_code: m.model_code || m.sku })))
    .sort((a, b) => a.builds - b.builds)
    .slice(0, 5);
  const maxCap = parts.length > 0 ? Math.max(...parts.map(p => p.builds)) : 0;

  /* ── Loading ───────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="h-screen w-screen bg-[#0a0a0a]">
        <LogoSpinner label="Loading Shop Floor" className="h-full" />
      </div>
    );
  }

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <div className="h-screen w-screen bg-[#0a0a0a] text-white overflow-hidden flex flex-col p-4 gap-3">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@700&display=swap');
        @keyframes ping-ring {
          0% { transform: translate(-50%,-50%) scale(1); opacity: 0.8; }
          100% { transform: translate(-50%,-50%) scale(4); opacity: 0; }
        }
        .leaflet-container { background: #0a0a0a; }
        .leaflet-control-attribution { display: none; }
        .shop-clock { font-family: 'Orbitron', sans-serif; }
      `}</style>

      {/* ════════ MARQUEE ════════ */}
      <div className="flex-none">
        <ShopMarquee
          available={available}
          flagCount={flags.length}
          droneCount={drones.length}
          buildCaps={buildCaps}
        />
      </div>

      {/* ════════ FORECAST CHART ════════ */}
      <Card className="flex-[1.2] min-h-0" label="Availability forecast">
        {/* Hero stats row */}
        <div className="flex items-center gap-6 mb-1 flex-none">
          <Heading color="bg-emerald-500/60">Availability Forecast</Heading>

          {todayData && (
            <div className="flex items-center gap-8 ml-auto">
              <div>
                <span className={`text-3xl font-bold tabular-nums ${getAvailColorClass(todayData.availableDrones, todayData.totalFleet)}`}>
                  {todayData.availableDrones?.toLocaleString()}
                </span>
                <span className="text-xs text-on-surface-variant ml-2">drones avail</span>
              </div>
              <div>
                <span className={`text-3xl font-bold tabular-nums ${getAvailColorClass(todayData.availableBatteries, todayData.totalBatteries)}`}>
                  {todayData.availableBatteries?.toLocaleString()}
                </span>
                <span className="text-xs text-on-surface-variant ml-2">batteries</span>
              </div>
              <Appear as="div" show={forecast?.totalPyro > 0}>
                <span className={`text-3xl font-bold tabular-nums ${todayData.availablePyro <= 0 ? 'text-red-400' : 'text-purple-400'}`}>
                  {todayData.availablePyro?.toLocaleString()}
                </span>
                <span className="text-xs text-on-surface-variant ml-2">pyro</span>
              </Appear>
            </div>
          )}
        </div>

        {/* Chart area */}
        <div className="flex-1 min-h-0">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="shopAvailStroke" x1="0" y1="0" x2="1" y2="0">
                    {availGradientStops.map((s, i) => (
                      <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={1} />
                    ))}
                  </linearGradient>
                  <linearGradient id="shopAvailFill" x1="0" y1="0" x2="1" y2="0">
                    {availGradientStops.map((s, i) => (
                      <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={0.15} />
                    ))}
                  </linearGradient>
                  <linearGradient id="shopBattStroke" x1="0" y1="0" x2="1" y2="0">
                    {battGradientStops.map((s, i) => (
                      <stop key={i} offset={s.offset} stopColor={s.color} stopOpacity={1} />
                    ))}
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" stroke="rgba(55,65,81,0.35)" vertical={false} />

                <XAxis
                  dataKey="date" tickFormatter={formatChartDate}
                  tick={{ fill: '#bbbbbb', fontSize: 13, fontWeight: 500 }}
                  axisLine={{ stroke: '#464646' }} tickLine={false}
                  interval="preserveStartEnd" minTickGap={60}
                />
                <YAxis
                  domain={[yMin, yMax]} allowDataOverflow
                  tick={{ fill: '#bbbbbb', fontSize: 11 }}
                  axisLine={false} tickLine={false} width={45}
                />

                {/* Reference lines */}
                <ReferenceLine x={todayStr} stroke="rgba(156,163,175,0.4)" strokeDasharray="4 4" />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" strokeWidth={2} />
                {forecast?.totalFleet && (
                  <ReferenceLine y={forecast.totalFleet} stroke="rgba(255,204,51,0.25)" strokeDasharray="6 3" />
                )}

                {/* Available for Shows line (after leases) */}
                <Line
                  type="monotone" dataKey="availableFleet"
                  stroke="rgba(251,146,60,0.5)" strokeWidth={1} strokeDasharray="4 4"
                  dot={false} activeDot={false} isAnimationActive={false}
                />

                {/* Available drones — color-shifting area */}
                <Area
                  type="monotone" dataKey="availableDrones"
                  fill="url(#shopAvailFill)" stroke="url(#shopAvailStroke)"
                  strokeWidth={2} dot={false} activeDot={false}
                  baseValue={0} isAnimationActive={false}
                />

                {/* Available batteries — dashed line */}
                <Line
                  type="monotone" dataKey="availableBatteries"
                  stroke="url(#shopBattStroke)" strokeWidth={1.5} strokeDasharray="6 3"
                  dot={false} activeDot={false} isAnimationActive={false}
                />

                {/* Available pyro — purple area */}
                {forecast?.totalPyro > 0 && (
                  <Area
                    type="monotone" dataKey="availablePyro"
                    fill="rgba(168,85,247,0.08)" stroke="#a855f7"
                    strokeWidth={1.5} dot={false} activeDot={false}
                    baseValue={0} isAnimationActive={false}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center">
              <EmptyState variant="area">No forecast data</EmptyState>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-1 flex-none text-[10px] text-on-surface-variant">
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-0.5 bg-emerald-400 rounded inline-block" /> Available Drones
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-0.5 border-t border-dashed border-emerald-300 inline-block" /> Batteries
          </span>
          <Appear show={forecast?.totalPyro > 0} className="flex items-center gap-1.5">
            <span className="w-4 h-0.5 bg-purple-400 rounded inline-block" /> Pyro
          </Appear>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-0.5 border-t border-dashed border-orange-400/50 inline-block" /> Available for Shows
          </span>
        </div>
      </Card>

      {/* ════════ BOTTOM SECTION: 2x2 widgets + map ════════ */}
      <div className="flex-1 min-h-0 grid gap-3" style={{ gridTemplateColumns: '1fr 1fr 1.3fr', gridTemplateRows: '1fr auto' }}>

        {/* ── Upcoming Events (top-left) ── */}
        <Card label="Upcoming events">
          <Heading color="bg-blue-500/60">Upcoming Events</Heading>
          <div className="flex-1 overflow-hidden space-y-1">
            {events.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <EmptyState variant="area">No upcoming events</EmptyState>
              </div>
            ) : (
              events.map(event => {
                const date = parseDate(event.event_date);
                const daysUntil = date?.diff(dayjs.utc().startOf('day'), 'day') ?? 0;
                const totalDrones = (parseInt(event.ls_drone_count) || 0) + (parseInt(event.pyro_drone_count) || 0);
                const status = getDisplayStatus(event);
                const colorClass = eventStatusColors[status] || eventStatusColors.pending;

                return (
                  <div key={event.id} className="flex items-center gap-3 rounded-md px-2 py-1.5">
                    <div className="flex-shrink-0 w-10 text-center">
                      {daysUntil <= 0 ? (
                        <div className="text-[11px] font-bold uppercase tracking-wide text-red-400 leading-none pt-1">Today</div>
                      ) : (
                        <>
                          <div className={`text-lg font-bold tabular-nums leading-tight ${
                            daysUntil <= 3 ? 'text-red-400' : daysUntil <= 7 ? 'text-orange-400' : 'text-blue-400'
                          }`}>
                            {daysUntil}
                          </div>
                          <div className="text-[9px] text-on-surface-variant uppercase">days</div>
                        </>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate">{event.name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-on-surface-variant">{formatEventDate(event.event_date, 'MMM D')}</span>
                        <span className="text-[10px] text-on-surface-variant">{totalDrones} drones</span>
                      </div>
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ring-1 ring-inset flex-shrink-0 ${colorClass}`}>
                      {status}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* ── Upcoming Deliveries (top-middle) ── */}
        <Card label="Upcoming deliveries">
          <Heading color="bg-yellow-500/60">Upcoming Deliveries</Heading>
          <div className="flex-1 overflow-hidden space-y-1">
            {deliveries.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <EmptyState variant="area">No upcoming deliveries</EmptyState>
              </div>
            ) : (
              deliveries.map(order => {
                const date = parseDate(order.delivery_date);
                const daysUntil = date?.diff(dayjs(), 'day') ?? 0;
                const statusColor = orderStatusColors[order.status] || orderStatusColors.pending;
                const typeBadge = orderTypeBadge[order.order_type] || orderTypeBadge.sale;

                return (
                  <div key={order.id} className="flex items-center gap-3 rounded-md px-2 py-1.5">
                    <div className="flex-shrink-0 w-10 text-center">
                      <div className={`text-lg font-bold tabular-nums leading-tight ${
                        daysUntil <= 3 ? 'text-red-400' : daysUntil <= 7 ? 'text-orange-400' : 'text-primary'
                      }`}>
                        {daysUntil}
                      </div>
                      <div className="text-[9px] text-on-surface-variant uppercase">days</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate tracking-[0.15em]">
                        {order.so_number || `Order #${order.id}`}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-on-surface-variant">
                          {formatEventDate(order.delivery_date, 'MMM D')}
                        </span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${typeBadge}`}>
                          {order.order_type}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* ── Mini Map (right, spans both rows) ── */}
        <div className="rounded-lg overflow-hidden border border-gray-700/40 relative" style={{ gridRow: '1 / 3' }}>
          <MapContainer
            center={[39.8283, -98.5795]}
            zoom={4}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            attributionControl={false}
            dragging={false}
            scrollWheelZoom={false}
            doubleClickZoom={false}
            touchZoom={false}
          >
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxNativeZoom={18}
              maxZoom={25}
              keepBuffer={4}
              updateWhenZooming={false}
              updateWhenIdle={true}
              crossOrigin="anonymous"
            />
            <MapAutoTour allPositions={mapPositions} liveStops={liveStops} />
            <ShopFloorClusterMarkers supercluster={supercluster} drones={validDrones} />
          </MapContainer>
          {/* Map overlay label */}
          <div className="absolute top-3 left-3 z-[1000] pointer-events-none">
            <div className="flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-md px-3 py-1.5 border border-gray-700/40">
              <div className="w-1 h-3 rounded-full bg-primary/60" />
              <span className="text-xs text-on-surface-variant uppercase tracking-widest font-semibold">Currently Active</span>
              <span className="text-[10px] text-primary/80 tabular-nums ml-1">{validDrones.length} drones</span>
              <Appear show={liveCount > 0} className="text-[10px] text-green-400/80 tabular-nums ml-1">● {liveCount} live</Appear>
            </div>
          </div>
        </div>

        {/* ── Flag Summary (bottom-left) ── */}
        <Card label="Flag summary">
          <Heading color="bg-orange-500/60" right={
            <Appear show={flags.length > 0} className="text-[10px] text-orange-400/80 bg-orange-400/10 px-2 py-0.5 rounded-full font-medium">
              {flags.length} total
            </Appear>
          }>
            Flag Summary
          </Heading>
          {flags.length === 0 ? (
            <FadeIn key="empty" className="flex-1 flex items-center justify-center">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                <span className="text-green-400 text-sm font-medium">No active flags</span>
              </div>
            </FadeIn>
          ) : (
            <FadeIn key="list" className="flex-1 overflow-hidden">
              <div className="flex rounded-full h-2.5 overflow-hidden mb-3 bg-gray-900/60">
                {flagCategories.map(([cat, count], i) => (
                  <div key={cat} className={flagColors[i % flagColors.length]}
                       style={{ width: `${(count / flags.length) * 100}%`, minWidth: '3px' }} />
                ))}
              </div>
              <div className="space-y-1">
                {flagCategories.slice(0, 4).map(([cat, count], i) => (
                  <div key={cat} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-sm flex-shrink-0 ${flagColors[i % flagColors.length]}`} />
                    <span className="text-xs text-on-surface flex-1 truncate">{cat}</span>
                    <span className="text-xs text-on-surface-variant tabular-nums font-medium">{count}</span>
                  </div>
                ))}
                <Reveal show={flagCategories.length > 4}>
                  <div className="text-[10px] text-on-surface-variant pl-4">+{flagCategories.length - 4} more</div>
                </Reveal>
              </div>
            </FadeIn>
          )}
        </Card>

        {/* ── Build Capacity by Part (bottom-middle) ── */}
        <Card label="Build capacity">
          <Heading color="bg-blue-500/60">Build Capacity</Heading>
          {parts.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState variant="area">No parts lists on the dashboard</EmptyState>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden space-y-1.5">
              {parts.map(p => {
                const isBottleneck = limitingLineIds.has(p.id);
                const pct = maxCap > 0 ? Math.max(5, Math.round((p.builds / maxCap) * 100)) : 0;
                const isPyro = p.variant === 'pyro';
                const barColor = isPyro
                  ? 'bg-gradient-to-r from-purple-600 to-purple-400'
                  : 'bg-gradient-to-r from-blue-600 to-blue-400';
                const barGlow = isPyro
                  ? '0 0 8px rgba(168,85,247,0.3)'
                  : '0 0 8px rgba(59,130,246,0.25)';

                return (
                  <div key={`${p.model_code}-${p.id}`} className="flex items-center gap-2">
                    <span className={`w-32 text-[10px] font-medium truncate ${
                      isBottleneck ? 'text-orange-400' : 'text-on-surface-variant'
                    }`}>
                      {p.model_code} {p.target_name}
                    </span>
                    <div className="flex-1 bg-gray-900/80 rounded-full h-3.5 overflow-hidden border border-gray-700/30">
                      <div
                        className={`h-full rounded-full ${barColor}`}
                        style={{ width: pct + '%', boxShadow: barGlow }}
                      />
                    </div>
                    <span className={`w-10 text-right text-[11px] tabular-nums ${
                      isBottleneck ? 'text-orange-400 font-bold' : 'text-on-surface-variant'
                    }`}>
                      {p.builds}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* ════════ FOOTER ════════ */}
      <div className="flex-none flex items-center justify-between px-1">
        <img
          src={fireflyLogo} alt="Firefly" className="h-10 object-contain"
          style={{ filter: 'drop-shadow(0 0 15px rgba(255,204,51,0.4))' }}
        />
        <div className="flex items-center gap-3">
          {/* Live indicator */}
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
          </span>
          <span className="shop-clock text-white text-2xl font-bold tabular-nums tracking-[0.2em]">
            {clock.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          </span>
        </div>
        <div className="text-on-surface-variant text-sm">
          Powered by <span className="text-primary font-medium">Firefleet</span>
        </div>
      </div>
    </div>
  );
};

export default ShopFloor;
